import React, { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
} from '@mui/material'
import {
  Edit as EditIcon,
  Assignment as InspectionIcon,
  ShowChart as TrendIcon,
  Notifications as AlertIcon,
  Settings as AlertRuleIcon,
  DeviceHub as DeviceIcon,
  ArrowBack as BackIcon,
  CalendarToday as DateIcon,
  WarningAmber as AttentionIcon,
} from '@mui/icons-material'
import { format, parseISO } from 'date-fns'
import { useAppDispatch, useAppSelector } from '@hooks/redux'
import { fetchApiariesAndGroups } from '@store/slices/locationsSlice'
import { fetchInspections } from '@store/slices/inspectionsSlice'
import { fetchAlerts } from '@store/slices/alertsSlice'
import SimpleHiveIcon from '@components/hives/SimpleHiveIcon'
import { Inspection } from '@types'

const formatDate = (value?: string | null) => {
  if (!value) return 'Not recorded'
  try {
    return format(parseISO(value), 'PPP')
  } catch {
    return value
  }
}

const inspectionDate = (inspection: Inspection) =>
  inspection.date || (inspection as { inspection_date?: string }).inspection_date

const impressionLabel = (value?: number) => {
  switch (value) {
    case 1:
      return { label: 'Needs attention', color: 'error' as const }
    case 2:
      return { label: 'Fair', color: 'warning' as const }
    case 3:
    case 4:
    case 5:
      return { label: 'Healthy', color: 'success' as const }
    default:
      return { label: 'No rating', color: 'default' as const }
  }
}

const HiveDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const hiveId = parseInt(id || '0', 10)
  const { hives, apiaries, loadingState: locationsLoading } = useAppSelector(
    (state) => state.locations,
  )
  const { inspections, loadingState: inspectionsLoading } = useAppSelector(
    (state) => state.inspections,
  )
  const { alerts } = useAppSelector((state) => state.alerts)
  const { devices } = useAppSelector((state) => state.devices)

  const hive = hives.find((item) => item.id === hiveId)
  const apiary = apiaries.find((item) => item.id === hive?.location_id)

  useEffect(() => {
    if (locationsLoading === 'idle') {
      dispatch(fetchApiariesAndGroups())
    }
  }, [dispatch, locationsLoading])

  useEffect(() => {
    if (hiveId) {
      dispatch(fetchInspections({ hiveId }))
      dispatch(fetchAlerts())
    }
  }, [dispatch, hiveId])

  const hiveInspections = useMemo(
    () =>
      inspections.filter(
        (inspection) => inspection.hive_id === hiveId || inspection.hive?.id === hiveId,
      ),
    [inspections, hiveId],
  )

  const hiveAlerts = useMemo(
    () =>
      alerts.filter(
        (alert) =>
          alert.is_active &&
          !alert.resolved_at &&
          (alert.hive === hiveId || alert.hive_name === hive?.name),
      ),
    [alerts, hiveId, hive?.name],
  )

  const device = devices.find(
    (item) => item.hive === hiveId || item.hive_id === hiveId,
  )

  if ((locationsLoading === 'pending' || locationsLoading === 'idle') && !hive) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  if (!hive) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Hive not found.
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/')}>
          Back to hives
        </Button>
      </Container>
    )
  }

  const impression = impressionLabel(hive.impression)
  const canEdit = hive.owner || hive.editable

  return (
    <Container sx={{ py: 3 }}>
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/')}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        All hives
      </Button>

      <Card
        sx={{
          mb: 3,
          borderTop: `4px solid ${hive.color || '#F8B133'}`,
          overflow: 'visible',
        }}
      >
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(248, 177, 51, 0.08)',
                  border: `1px solid ${hive.color || '#F8B133'}`,
                }}
              >
                <SimpleHiveIcon size="xl" color={hive.color} />
              </Box>
            </Grid>
            <Grid item xs>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                  {hive.name}
                </Typography>
                {hive.attention && (
                  <Chip
                    icon={<AttentionIcon />}
                    label="Needs attention"
                    color="warning"
                    size="small"
                  />
                )}
                <Chip label={impression.label} color={impression.color} size="small" />
              </Stack>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                {apiary?.name || hive.location_name || 'No apiary assigned'}
                {hive.bee_race ? ` · ${hive.bee_race}` : ''}
                {hive.label ? ` · ${hive.label}` : ''}
              </Typography>
              {hive.notes && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {hive.notes}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ mt: 3 }}
            flexWrap="wrap"
            useFlexGap
          >
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/hives/${hive.id}/edit`)}
              >
                Edit hive
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<InspectionIcon />}
              onClick={() => navigate(`/inspections/create?hiveId=${hive.id}`)}
            >
              New inspection
            </Button>
            <Button
              variant="outlined"
              startIcon={<TrendIcon />}
              onClick={() => navigate(`/measurements/${hive.id}`)}
            >
              Trends
            </Button>
            <Button
              variant="outlined"
              startIcon={<AlertIcon />}
              onClick={() => navigate(`/alerts?search=${encodeURIComponent(hive.name)}`)}
            >
              Alerts
            </Button>
            <Button
              variant="outlined"
              startIcon={<AlertRuleIcon />}
              onClick={() => navigate('/alertrules')}
            >
              Alert rules
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Last inspection
              </Typography>
              <Typography variant="h6">
                {formatDate(hive.last_inspection_date)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Active alerts
              </Typography>
              <Typography variant="h6" color={hiveAlerts.length ? 'error.main' : 'text.primary'}>
                {hiveAlerts.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Monitor
              </Typography>
              <Typography variant="h6">
                {device?.name || 'No device'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {device?.is_online || device?.online ? 'Online' : device ? 'Offline' : 'Attach a device to this hive'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Queen
              </Typography>
              <Typography variant="h6">
                {hive.queen?.name || hive.queen?.breed || 'Not recorded'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {hiveAlerts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {hiveAlerts[0].title || hiveAlerts[0].message}
          {hiveAlerts.length > 1 ? ` · ${hiveAlerts.length - 1} more active alert${hiveAlerts.length > 2 ? 's' : ''}` : ''}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Inspections</Typography>
                <Button
                  size="small"
                  onClick={() => navigate(`/hives/${hive.id}/inspections`)}
                >
                  View all
                </Button>
              </Box>
              {inspectionsLoading === 'pending' ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : hiveInspections.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No inspections yet. Record the first check so this hive has a field history.
                </Typography>
              ) : (
                hiveInspections.slice(0, 5).map((inspection, index) => (
                  <Box key={inspection.id}>
                    {index > 0 && <Divider sx={{ my: 1.5 }} />}
                    <Box
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/hives/${hive.id}/inspections/${inspection.id}`)}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <DateIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(inspectionDate(inspection))}
                        </Typography>
                        {inspection.attention && (
                          <Chip size="small" color="warning" label="Follow up" />
                        )}
                      </Stack>
                      <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                        {(inspection as { title?: string }).title || `Inspection #${inspection.id}`}
                      </Typography>
                      {inspection.notes && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {inspection.notes}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Apiary
              </Typography>
              <Typography variant="body1">{apiary?.name || hive.location_name || 'Unassigned'}</Typography>
              {apiary?.address && (
                <Typography variant="body2" color="text.secondary">
                  {apiary.address}
                </Typography>
              )}
              {apiary && (apiary.owner || apiary.editable) && (
                <Button
                  sx={{ mt: 2 }}
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/apiaries/${apiary.id}/edit`)}
                >
                  Edit apiary
                </Button>
              )}
            </CardContent>
          </Card>

          {device && (
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <DeviceIcon color="action" />
                  <Typography variant="h6">Device</Typography>
                </Stack>
                <Typography variant="body1">{device.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Last seen: {formatDate(device.last_message_received || device.last_seen)}
                </Typography>
                <Button
                  sx={{ mt: 2 }}
                  onClick={() => navigate(`/devices/${device.id}`)}
                >
                  Open device
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Container>
  )
}

export default HiveDetail
